import { useState, type ReactNode } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { DialogFrame } from "@/components/common/DialogFrame";
import { getRunnerPackage, type DeviceLicenseSummary, type Runner } from "@/lib/api";
import { noFreeDeviceLicenseMessage } from "@/lib/runner";
import { EnvironmentSourcesLink } from "./EnvironmentSourcesLink";
import { RunnerEnrollmentCodeIssuer } from "./RunnerEnrollmentCodeIssuer";
import { RunnerLinkForm } from "./RunnerLinkForm";
import { RunnerPackageDownload } from "./RunnerPackageDownload";
import { useDeviceLicenseSummary } from "./useDeviceLicenseSummary";
import { useRunnerDownloadInfo } from "./useRunnerDownloadInfo";

const SETUP_APP_PATH = "/opt/izanagi-device/bin/izanagi-device-setup";

interface AddRunnerDialogProps {
	onRegistered: (runner: Runner) => void;
	onClose: () => void;
}

/**
 * デバイスの追加の手順。.deb を入れ、設定アプリに登録用のコードを貼り付けて出た確認コードで登録する。
 * 開いただけでは登録用のコードも登録も作らない。開くたびにマウントし直し、前回の状態を持ち越さない。
 * 空いているライセンスが無ければ、.deb を入れてから承認で断られないよう、手順 1 で理由を出し、ダウンロードと登録用のコードの発行をさせない。
 */
export function AddRunnerDialog({ onRegistered, onClose }: AddRunnerDialogProps) {
	const deb = useRunnerDownloadInfo(getRunnerPackage, ".deb の情報の取得に失敗しました。");
	const [licenseRefreshKey, setLicenseRefreshKey] = useState(0);
	const licenses = useDeviceLicenseSummary(licenseRefreshKey);
	const noFreeLicense =
		licenses.summary !== null && licenses.summary.freeCount === 0
			? noFreeDeviceLicenseMessage(licenses.summary.licenseCount)
			: null;

	return (
		<DialogFrame
			open
			title="デバイスを追加"
			width={640}
			onClose={onClose}
			footer={
				<ConfirmButton
					label="閉じる"
					loading={false}
					variant="ghost"
					onClick={onClose}
					data-testid="add-runner-close"
				/>
			}
		>
			<Step number={1} title="デバイスに .deb をダウンロード">
				<LicenseAvailability
					summary={licenses.summary}
					error={licenses.error}
					noFreeLicense={noFreeLicense}
				/>
				<div data-testid="add-runner-package" style={captionStyle}>
					<RunnerPackageDownload
						release={deb.info}
						error={deb.error}
						onRetry={deb.retry}
						disabled={noFreeLicense !== null}
					>
						<EnvironmentSourcesLink />
					</RunnerPackageDownload>
				</div>
			</Step>
			<Step number={2} title="インストール">
				<div
					data-testid="add-runner-install"
					style={{ display: "flex", flexDirection: "column", gap: 4 }}
				>
					<Caption>
						ダウンロードした .deb を右クリックして「別のアプリケーションで開く」から「App
						Center」を選び、「インストール」を押して管理者のパスワードを入力します。
					</Caption>
					{deb.info && (
						<>
							<Caption>または、ダウンロードしたフォルダで端末を開いて次を実行します。</Caption>
							<CopyableCommand
								command={`sudo apt install ./izanagi-device_${deb.info.version}_*.deb`}
							/>
						</>
					)}
				</div>
			</Step>
			<Step number={3} title="「Izanagi デバイスを登録」を開く">
				<Caption testId="add-runner-setup">
					アプリ一覧から開き、最初の画面に登録用のコードを貼り付けると、確認コードが表示されます。画面が無ければ端末で{" "}
					<InlineCode>{SETUP_APP_PATH}</InlineCode> を実行します。
				</Caption>
				<RunnerEnrollmentCodeIssuer unavailable={noFreeLicense !== null} />
			</Step>
			<Step number={4} title="確認コードを入力">
				<RunnerLinkForm
					onRegistered={(runner) => {
						setLicenseRefreshKey((key) => key + 1);
						onRegistered(runner);
					}}
				/>
			</Step>
		</DialogFrame>
	);
}

const captionStyle = { color: "var(--fg-2)", fontSize: "var(--text-caption)" };
const codeStyle = { color: "var(--fg-1)", fontSize: "var(--text-caption)" };

function LicenseAvailability({
	summary,
	error,
	noFreeLicense,
}: {
	summary: DeviceLicenseSummary | null;
	error: string | null;
	noFreeLicense: string | null;
}) {
	if (error !== null)
		return (
			<p
				data-testid="add-runner-licenses-error"
				style={{ ...captionStyle, margin: 0, color: "var(--danger)" }}
			>
				{error}
			</p>
		);
	if (summary === null) return null;
	return (
		<div
			data-testid="add-runner-licenses"
			style={{ display: "flex", flexDirection: "column", gap: 4 }}
		>
			<p style={{ ...captionStyle, margin: 0, fontVariantNumeric: "tabular-nums" }}>
				空いているライセンス {summary.freeCount} 台 (ライセンス {summary.licenseCount} 台)
			</p>
			{noFreeLicense && (
				<p
					data-testid="add-runner-no-free-license"
					style={{ ...captionStyle, margin: 0, color: "var(--warning)" }}
				>
					{noFreeLicense}
				</p>
			)}
		</div>
	);
}

function Step({ number, title, children }: { number: number; title: string; children: ReactNode }) {
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<p
				data-testid={`add-runner-step-${number}`}
				style={{ margin: 0, color: "var(--fg-1)", fontSize: "var(--text-ui)", fontWeight: 600 }}
			>
				{number}. {title}
			</p>
			{children}
		</div>
	);
}

function Caption({ testId, children }: { testId?: string; children: ReactNode }) {
	return (
		<p data-testid={testId} style={{ ...captionStyle, margin: 0 }}>
			{children}
		</p>
	);
}

function InlineCode({ children }: { children: ReactNode }) {
	return (
		<code className="select-text font-mono" style={codeStyle}>
			{children}
		</code>
	);
}

function CopyableCommand({ command }: { command: string }) {
	return (
		<div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
			<InlineCode>{command}</InlineCode>
			<CopyIconButton value={command} label="コマンドをコピー" />
		</div>
	);
}
